import "./Footer.css";
import Button from "../../../../../shared/components/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  saveAnswerRequest,
  fetchExamQuestionRequest,
  setCurrentQuestionIndex,
  setActiveSection,
  updateQuestionStatus,
  hidePracticeProgressiveMessage,
} from "../../../../../redux/exam/examSlice";
import { postAdaptiveSingleSaveRequest } from "../../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import { getBackendQuestionStatus } from "../../../../../shared/utilities/helpers/examQuestionStatus";
import { getExamSectionKey } from "../../../../../shared/utilities/helpers/examSectionKey";

const Footer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    currentQuestionIndex,
    questionsData,
    testDetails,
    paperId,
    testType,
    loading,
  } = useSelector((state) => state.examSlice);

  const currentQuestion = questionsData[currentQuestionIndex];
  const isAdaptiveMode = location?.state?.useAdaptiveTestScreen === true;
  const isPracticeMode =
    !isAdaptiveMode && testDetails?.practiceMode === "practice";
  const adaptiveQuestionId =
    location?.state?.adaptive_question_id ??
    location?.state?.adaptiveQuestionId ??
    null;
  const adaptivePaperType = Number(location?.state?.paper_type ?? 20);
  const isPracticeGamified =
    isPracticeMode && testDetails?.gamificationOn === true;

  const isLastQuestion =
  currentQuestionIndex === questionsData.length - 1;

  const getSectionKey = (q) => getExamSectionKey(q);
  const moveToQuestion = (question) => {
    if (!question?.qid) return;

    dispatch(
      updateQuestionStatus({
        qid: question.qid,
        makeCurrent: true,
        testId: testDetails?.testId,
        setId: testDetails?.setId,
      })
    );

    dispatch(
      fetchExamQuestionRequest({
        qid: question.qid,
        paperId: question.paper_id,
        paperType: isAdaptiveMode ? adaptivePaperType : undefined,
        adaptive_question_id: isAdaptiveMode ? adaptiveQuestionId : undefined,
        testId: testDetails?.testId,
        setId: testDetails?.setId,
      })
    );
  };
  const buildAdaptiveSingleSavePayload = (question) => {
    const answerType = Number(question?.answer_type_id);
    const selectedOptionRaw = question?.selectedOption;
    const optionId = Array.isArray(selectedOptionRaw)
      ? selectedOptionRaw
      : selectedOptionRaw
        ? [selectedOptionRaw]
        : [];

    return {
      option_id: optionId,
      qid: question?.qid,
      time: Number(question?.time ?? question?.time_taken ?? 0),
      adaptive_question_id: adaptiveQuestionId,
      question_status: Number(getBackendQuestionStatus(question) ?? 0),
      paper_type: adaptivePaperType,
      option_int:
        answerType === 3
          ? question?.integerAnswer ?? question?.integer_Answer ?? ""
          : "",
    };
  };
  const goToNextQuestion = (currentQ) => {
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= questionsData.length) return;

    const nextQuestion = questionsData[nextIndex];
    dispatch(setCurrentQuestionIndex(nextIndex));
    const nextSection = getSectionKey(nextQuestion);
    const currSection = getSectionKey(currentQ);

    if (nextSection !== currSection) {
      dispatch(setActiveSection(nextSection));
    }

    if (isAdaptiveMode) {
      moveToQuestion(nextQuestion);
    }
  };

  const handleIDontKnow = () => {
    if (loading || !isAdaptiveMode || !currentQuestion) return;

    const answerType = Number(currentQuestion?.answer_type_id);
    const resetSelection = answerType === 2 ? [] : "";

    const unansweredQuestion = {
      ...currentQuestion,
      selectedOption: resetSelection,
      integerAnswer: "",
      integer_Answer: "",
      marked: false,
      question_status: 2,
      status: "unanswered",
    };

    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        selectedOption: resetSelection,
        integerAnswer: "",
        integerChecked: false,
        answerChecked: false,
        showSolution: false,
        markReview: false,
      })
    );

    dispatch(postAdaptiveSingleSaveRequest(buildAdaptiveSingleSavePayload(unansweredQuestion)));
    goToNextQuestion(currentQuestion);
  };

  const handleSaveNext = (e) => {
    e.preventDefault();

    if (loading) return;

    const currentQ = currentQuestion;
    if (!currentQ) return;

    const answerType = Number(currentQ?.answer_type_id);
    const hasSelectedMcq =
      answerType === 2
        ? Array.isArray(currentQ?.selectedOption) && currentQ.selectedOption.length > 0
        : currentQ?.selectedOption !== null &&
          currentQ?.selectedOption !== undefined &&
          currentQ?.selectedOption !== "";

    const hasCheckedInteger = currentQ?.integerChecked === true;

    // If the user already got instant-feedback for this question,
    // avoid triggering streak again on "Save & Next".
    const triggerStreak =
      !isPracticeGamified
        ? true
        : answerType === 3
        ? !hasCheckedInteger
        : !hasSelectedMcq;

    // Prevent the same streak message from coming back again after
    // loading-triggered unmount/remount on "Save & Next".
    if (isPracticeGamified) {
      dispatch(hidePracticeProgressiveMessage());
    }

    const validPaperId = paperId;
    const validPaperType = testDetails?.paper_type || testType?.id;

    // if (!validPaperId) {
    //   console.error("paperId is missing!", { testDetails, paperId });
    //   alert("Exam data missing. Please restart test.");
    //   return;
    // }

    // 1. Save current answer
    dispatch(
      isAdaptiveMode
        ? postAdaptiveSingleSaveRequest(buildAdaptiveSingleSavePayload(currentQ))
        : saveAnswerRequest({
            // paper_type: validPaperType,
            // paper_id: validPaperId,
              paper_type: testDetails?.paper_type || testType?.id,
          paper_id: paperId,
            qid: currentQ?.qid,
            // option_id: currentQ?.selectedOption || "",
            test_id: testDetails?.testId,
            set_id: testDetails?.setId,
            time: currentQ?.time,
            triggerStreak,
            question_status: getBackendQuestionStatus(currentQ),
              ...(currentQ.type === "Integer-Type"
            ? { option_int: currentQ.integerAnswer || "" }
            : { option_id: currentQ.selectedOption || "" }),
          })
    );
    if (isLastQuestion) {
      dispatch(
        updateQuestionStatus({
          qid: currentQ.qid,
          moveAway: true,
        })
      );
      return;
    }
    goToNextQuestion(currentQ);
  };

  const handlePrevious = () => {
    if (currentQuestionIndex === 0) return;

    const prevIndex = currentQuestionIndex - 1;
    const prevQuestion = questionsData[prevIndex];

    const validPaperId = testDetails?.paper_id || paperId;
    const validPaperType = testDetails?.paper_type || testType?.id;

    dispatch(setCurrentQuestionIndex(prevIndex));

    const prevSection = getSectionKey(prevQuestion);
    const currSection = getSectionKey(currentQuestion);
    if (prevSection !== currSection) {
      dispatch(setActiveSection(prevSection));
    }

    dispatch(
      fetchExamQuestionRequest({
        paperId: validPaperId,
        paperType: isAdaptiveMode ? adaptivePaperType : validPaperType,
        adaptive_question_id: isAdaptiveMode ? adaptiveQuestionId : undefined,
        qid: prevQuestion.qid,
        testId: testDetails?.testId,
      })
    );
  };

  const handleMarkReview = () => {
    if (currentQuestion) {
      dispatch(
        updateQuestionStatus({
          qid: currentQuestion.qid,
          markReview: true,
        })
      );
    }
  };

  const hasCurrentAnswer = () => {
    if (!currentQuestion) return false;
    const answerType = Number(currentQuestion?.answer_type_id);

    if (answerType === 3) {
      return (
        String(
          currentQuestion?.integerAnswer ?? currentQuestion?.integer_Answer ?? ""
        ).trim() !== ""
      );
    }

    return answerType === 2
      ? Array.isArray(currentQuestion?.selectedOption) &&
          currentQuestion.selectedOption.length > 0
      : currentQuestion?.selectedOption !== null &&
          currentQuestion?.selectedOption !== undefined &&
          currentQuestion?.selectedOption !== "";
  };

  const handlePracticeAction = () => {
    if (!currentQuestion) return;
    const answerType = Number(currentQuestion?.answer_type_id);
    const alreadyChecked = currentQuestion?.answerChecked === true;

    if (alreadyChecked) {
      dispatch(
        updateQuestionStatus({
          qid: currentQuestion.qid,
          showSolution: true,
        })
      );
      return;
    }

    if (!hasCurrentAnswer()) return;

    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        answerChecked: true,
        showSolution: false,
        integerChecked: answerType === 3 ? true : currentQuestion?.integerChecked,
      })
    );

    dispatch(
      isAdaptiveMode
        ? postAdaptiveSingleSaveRequest(
            buildAdaptiveSingleSavePayload(currentQuestion)
          )
        : saveAnswerRequest({
            paper_type: testDetails?.paper_type || testType?.id,
            paper_id: paperId,
            qid: currentQuestion?.qid,
            test_id: testDetails?.testId,
            set_id: testDetails?.setId,
            time: currentQuestion?.time,
            triggerStreak: isPracticeGamified,
            question_status: getBackendQuestionStatus(currentQuestion),
            ...(answerType === 3
              ? {
                  option_int:
                    currentQuestion?.integerAnswer ?? currentQuestion?.integer_Answer ?? "",
                }
              : { option_id: currentQuestion?.selectedOption || "" }),
            skipAdvance: true,
          })
    );
  };

  const handleViewSolution = () => {
    if (!currentQuestion || !hasCurrentAnswer()) return;
    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        showSolution: true,
      })
    );
  };

  return (
    <>
      {isAdaptiveMode && (
        <div className="adaptive-idk-row">
          <Button
            title="I don't know"
            variant="outlined"
            color="primary"
            className="footer-btn outlined-btn adaptive-idk-btn"
            onClick={handleIDontKnow}
            disabled={loading}
          />
        </div>
      )}
      <div className="footer-container">
        <div className="footer-left">
        <Button
          title="Previous"
         variant="outlined"
         color="primary"
          className="footer-btn outlined-btn"
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        />
        {isPracticeGamified ? (
          <Button
            title="View Solution"
            variant="outlined"
            color="primary"
            className="footer-btn outlined-btn"
            onClick={handleViewSolution}
            disabled={!hasCurrentAnswer()}
          />
        ) : isPracticeMode ? (
          <Button
            title={currentQuestion?.answerChecked ? "View Solution" : "Check Answer"}
            variant="outlined"
            color="primary"
            className="footer-btn outlined-btn"
            onClick={handlePracticeAction}
            disabled={!currentQuestion?.answerChecked && !hasCurrentAnswer()}
          />
        ) : isAdaptiveMode ? null : (
          <Button
            title="Mark for Review"
            variant="outlined"
            color="primary"
            className="footer-btn outlined-btn"
            onClick={handleMarkReview}
          />
        )}
        </div>

        <div className="footer-right">
          <Button
            // title="Save & Next"
            variant="contained"
            color="error"
             title={isLastQuestion ? "Save" : "Save & Next"}
            className="footer-btn contained-error"
            onClick={handleSaveNext}
            // disabled={
            //   currentQuestionIndex === questionsData.length - 1 || loading
            // }
            disabled={loading || !hasCurrentAnswer()}
          />
        </div>
      </div>
    </>
  );
};

export default Footer;
